import {
  Progress,
  Tag,
  Space,
  Typography,
  Divider,
  Row,
  Col,
  Flex,
} from "antd";
import { calculation } from "../data/calculationData";
import { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import {
  calculateRepairRecommendation,
  RecommendationType,
} from "../lib/calculateRecommendationScore";
import { Card } from "@rumbledirect/ui";

const { Text, Title } = Typography;

interface ClaimDossierRecommendationCardProps {
  isLoading: boolean;
  onClickDetails?: () => void;
  dossier?: Dossier;
}

const recommendationLabelMap: Record<RecommendationType, string> = {
  repair: "Advies: herstellen",
  unsure: "Advies: nader beoordelen",
  totaled: "Advies: (economisch) total loss",
};

const adviesTagColorMap: Record<
  RecommendationType,
  "green" | "orange" | "red"
> = {
  repair: "green",
  unsure: "orange",
  totaled: "red",
};

const getStrokeColor = (score: number) => {
  if (score >= 70) return "#52c41a";
  if (score >= 40) return "#faad14";
  return "#ff4d4f";
};

export const ClaimDossierRecommendationCard = ({
  dossier,
  isLoading,
  onClickDetails,
}: ClaimDossierRecommendationCardProps) => {
  const criteria = [
    {
      key: "looptijd",
      label: "Resterende looptijd",
      value: "24 maanden",
      explanation:
        "Nog aanzienlijke contractduur; herstel is beter verdedigbaar.",
    },
    {
      key: "kilometrage",
      label: "Kilometrage",
      value: `${dossier?.kilometers?.toLocaleString("nl-NL")} km`,
      explanation: "Hoger kilometrage drukt de restwaarde van het voertuig.",
    },
    {
      key: "leeftijd",
      label: "Voertuigleeftijd",
      value: dossier?.vehicle?.buildYear
        ? `${new Date().getFullYear() - dossier.vehicle.buildYear} jaar`
        : "Onbekend",
    },
  ];

  const { estimatedVehicleValue, repairScore, recommendationType } =
    calculateRepairRecommendation({
      kilometers: dossier?.kilometers || 0,
      buildYear: dossier?.vehicle?.buildYear || new Date().getFullYear(),
      calculationTotal: calculation.totals.inclVat,
      listPrice: dossier?.vehicle?.listPrice || 0,
    });

  return (
    <Card
      loading={isLoading}
      title="Hersteladvies"
      extra={
        !isLoading && (
          <Tag color={adviesTagColorMap[recommendationType]}>
            {recommendationLabelMap[recommendationType]}
          </Tag>
        )
      }
      style={{ width: "100%" }}
    >
      <Row gutter={24} align="middle">
        <Col xs={24} sm={8} style={{ textAlign: "center", marginBottom: 16 }}>
          <Progress
            type="circle"
            percent={repairScore}
            strokeColor={getStrokeColor(repairScore)}
            size={110}
          />
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">Herstel-score</Text>
          </div>
        </Col>

        <Col xs={24} sm={16}>
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Row gutter={16}>
              <Col span={12}>
                <Text type="secondary">Ontvangen calculatie</Text>
                <br />
                <Title level={5} style={{ margin: 0 }}>
                  € {calculation.totals.inclVat.toFixed(2).replace(".", ",")}{" "}
                  <Text type="secondary" style={{ fontWeight: "normal" }}>
                    incl. BTW
                  </Text>
                </Title>
              </Col>
              <Col span={12}>
                <Text type="secondary">Geschatte voertuigwaarde</Text>
                <br />
                <Title level={5} style={{ margin: 0 }}>
                  € {estimatedVehicleValue.toFixed(2).replace(".", ",")}{" "}
                  <Text type="secondary" style={{ fontWeight: "normal" }}>
                    {" "}
                    incl. BTW
                  </Text>
                </Title>
              </Col>
            </Row>

            <Divider style={{ margin: "8px 0" }} />

            <div>
              <Text strong>Belangrijkste criteria</Text>
              <div style={{ marginTop: 8 }}>
                {criteria.map((item) => (
                  <Row
                    key={item.key}
                    style={{
                      marginBottom: 6,
                      alignItems: "baseline",
                    }}
                  >
                    <Flex vertical>
                      <Text>{item.label}</Text>
                      <Flex>
                        <Text strong>{item.value}</Text>
                        {item.explanation && (
                          <Text type="secondary" style={{ marginLeft: 8 }}>
                            – {item.explanation}
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                  </Row>
                ))}
              </div>
            </div>

            {onClickDetails && (
              <Text
                type="secondary"
                style={{ cursor: "pointer" }}
                onClick={onClickDetails}
                underline
              >
                Bekijk volledige berekening en onderbouwing
              </Text>
            )}
          </Space>
        </Col>
      </Row>
    </Card>
  );
};
